'use client';

import { useFormValidation } from '@react-aria/form';
import { useFormReset, useId, useSlotId } from '@react-aria/utils';
import { useFormValidationState } from '@react-stately/form';
import { useControlledState } from '@react-stately/utils';
import clsx from 'clsx';
import { type ForwardedRef, type UIEventHandler, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Button as RACButton,
  Collection as RACCollection,
  ComboBox as RACComboBox,
  Group as RACGroup,
  Header as RACHeader,
  Input as RACInput,
  ListBoxItem as RACListBoxItem,
  ListBoxSection as RACListBoxSection,
  Text as RACText,
  type ComboBoxProps as RACComboBoxProps,
  type ListBoxItemProps as RACListBoxItemProps,
  Provider as RACProvider,
  TextContext as RACTextContext,
  GroupContext as RACGroupContext,
  FieldErrorContext as RACFieldErrorContext,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/localization-provider.js';
import { IconChevronDown } from '../../icons/index.js';
import { isProduction } from '../../utils/is-production.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { CheckboxIndicator } from '../internal/checkbox-indicator/checkbox-indicator.js';
import { CollapsibleList } from '../internal/collapsible-list/collapsible-list.js';
import { FormFieldDescription, FormFieldError, FormFieldLabel } from '../internal/form-field/form-field.js';
import type { TagGroupProps } from '../tag/tag-group.js';
import type {
  ApiProps,
  CollectionItem,
  CollectionProps,
  CommonProps,
  FieldWithPlaceholderProps,
  Key,
  StringLikeChildren,
} from '../types.js';
import { textStyle } from '../typography/utils.js';
import { TagFieldInput } from './tag-field-input.js';
import { TagFieldListBox } from './tag-field-list-box.js';
import { TagFieldTags } from './tag-field-tags.js';

/** Represents the state of the `TagField` component for validation purposes. */
export interface TagFieldValidationValue {
  /** The selected keys in the TagField. */
  selectedKeys: Set<Key> | null;
  /** The value of the TagField input. */
  inputValue: string;
}

export interface TagFieldProps<T extends CollectionItem = CollectionItem>
  extends
    CommonProps,
    FieldWithPlaceholderProps<TagFieldValidationValue>,
    CollectionProps<T>,
    ApiProps<TagFieldApi>,
    Pick<
      RACComboBoxProps<T>,
      | 'isRequired'
      | 'isDisabled'
      | 'isInvalid'
      | 'isReadOnly'
      | 'inputValue'
      | 'defaultInputValue'
      | 'autoFocus'
      | 'onInputChange'
      | 'onFocus'
      | 'onBlur'
      | 'onOpenChange'
    >,
    Pick<TagGroupProps<T>, 'renderEmptyState'> {
  /** The currently selected keys in the collection (controlled). */
  selectedKeys?: Iterable<Key>;
  /** The initial selected keys in the collection (uncontrolled). */
  defaultSelectedKeys?: Iterable<Key>;
  /** Handler that is called when the selection changes. */
  onSelectionChange?: (keys: Set<Key>) => void;
  /**
   * Whether the list of options should be virtualized. Use this as a performance optimization for large lists.
   * @default false
   */
  isVirtualized?: boolean;
  /** Handler that is called when the list of items is scrolled. */
  onScroll?: UIEventHandler<HTMLDivElement>;
}

/** The imperative API exposed by the `TagField` component. */
export interface TagFieldApi {
  /** Focuses the input field and opens the list of items. */
  focus: () => void;
}

function TagField<T extends CollectionItem>(
  {
    label,
    description,
    error,
    items,
    children,
    placeholder,
    UNSAFE_className,
    UNSAFE_style,
    isReadOnly,
    inputValue: consumerInputValue,
    defaultInputValue: defaultConsumerInputValue,
    onInputChange,
    form,
    name,
    validate,
    onScroll,
    selectedKeys: consumerSelectedKeys,
    defaultSelectedKeys: defaultConsumerSelectedKeys,
    onSelectionChange,
    id,
    renderEmptyState,
    isVirtualized = false,
    apiRef,
    ...props
  }: TagFieldProps<T>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const inputRef = useRef<HTMLInputElement>(null);

  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, isRequired, isDisabled, isInvalid } = props;

  useProductionWarning(() => {
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn('TagField requires one of label / aria-label / aria-labelledby for accessibility');
    }
  }, [label, ariaLabel, ariaLabelledBy]);

  if (!isProduction() && items != null && typeof children !== 'function') {
    console.warn('`TagField` requires `children` to be a function when using `items` prop');
  }

  const messages = useLocalizedMessages('tagField');

  useImperativeHandle(
    apiRef,
    () => ({
      focus: () => inputRef.current?.focus(),
    }),
    [],
  );

  // State

  const [defaultSelectedKeys] = useState(() => defaultConsumerSelectedKeys ?? new Set<Key>());
  const [selectedKeys, setSelectedKeys] = useControlledState(
    consumerSelectedKeys,
    defaultSelectedKeys,
    onSelectionChange,
  );
  const [inputValue, setInputValue] = useControlledState(
    consumerInputValue,
    defaultConsumerInputValue ?? '',
    onInputChange,
  );

  const selectedSet = useMemo(() => {
    // eslint-disable-next-line unicorn/no-instanceof-builtins
    if (selectedKeys instanceof Set) {
      return new Set<Key>(selectedKeys.values());
    }

    return new Set<Key>(selectedKeys);
  }, [selectedKeys]);

  // Validation

  const inputId = useId(id);
  const formInputRef = useRef<HTMLInputElement>(null);

  const validationState = useFormValidationState({
    name,
    value: useMemo(() => ({ selectedKeys: selectedSet, inputValue }), [selectedSet, inputValue]),
    isInvalid,
    isRequired,
    validate,
    validationBehavior: 'native',
  });

  const [initialSelectedKeys] = useState(selectedKeys);
  const [initialInputValue] = useState(inputValue);

  useFormReset<[Iterable<Key>, string]>(
    formInputRef,
    [defaultConsumerSelectedKeys ?? initialSelectedKeys, defaultConsumerInputValue ?? initialInputValue],
    ([a, b]) => {
      setSelectedKeys(a);
      setInputValue(b);
    },
  );

  useFormValidation(
    {
      focus: () => {
        // Querying by element ID because a ref is always null here, I don't know why :(
        document.getElementById(inputId)?.focus();
      },
      isInvalid,
      isRequired,
      validate,
      validationBehavior: 'native',
    },
    validationState,
    formInputRef,
  );

  // Event handlers

  // Commit validation on input blur if the values have changed
  const inputValueOnFocus = useRef(inputValue);
  const selectedSetOnFocus = useRef(selectedSet);

  const onFocusChange = (isFocused: boolean) => {
    if (isFocused) {
      inputValueOnFocus.current = inputValue;
      selectedSetOnFocus.current = selectedSet;
    } else if (inputValue !== inputValueOnFocus.current || selectedSet !== selectedSetOnFocus.current) {
      validationState.commitValidation();
    }
  };

  const onItemSelectionChange = (key: Key) => {
    if (selectedSet.has(key)) {
      // This is a temporary mutation followed immediately by a state update, so this is fine
      // eslint-disable-next-line react-hooks/immutability
      selectedSet.delete(key);
    } else {
      selectedSet.add(key);
    }

    setSelectedKeys(new Set(selectedSet.values()));
    validationState.commitValidation();
  };

  // Text slots

  const descriptionId = useSlotId([Boolean(description)]);

  const errorMessageId = useSlotId([
    Boolean(error || validationState.displayValidation.validationErrors),
    validationState.displayValidation.isInvalid,
  ]);

  return (
    <>
      <RACComboBox
        {...props}
        ref={ref}
        id={inputId}
        className={clsx('cim-combo-box', 'cim-tag-field', UNSAFE_className)}
        style={UNSAFE_style}
        // Force no internal selection - we handle selection manually
        selectedKey={null}
        inputValue={inputValue}
        onInputChange={setInputValue}
        menuTrigger="focus"
        isReadOnly={isReadOnly}
        data-readonly={isReadOnly ? true : undefined}
        onSelectionChange={(key) => {
          if (key) {
            onItemSelectionChange(key);
          }
        }}
      >
        <RACProvider
          values={[
            [RACTextContext, { slots: { description: { id: descriptionId }, errorMessage: { id: errorMessageId } } }],
            [RACGroupContext, { isInvalid: validationState.displayValidation.isInvalid, isDisabled }],
            [RACFieldErrorContext, validationState.displayValidation],
          ]}
        >
          <FormFieldLabel isRequired={isRequired} isDisabled={isDisabled}>
            {label}
          </FormFieldLabel>

          <RACGroup className="cim-combo-box-input-group">
            <TagFieldInput
              inputRef={inputRef}
              aria-describedby={props['aria-describedby']}
              isReadOnly={isReadOnly}
              isRequired={isRequired}
              isInvalid={validationState.displayValidation.isInvalid}
              placeholder={
                selectedSet.size === 0 && placeholder
                  ? placeholder
                  : messages.format('itemsSelected', { selectedItemCount: selectedSet.size })
              }
              onFocusChange={onFocusChange}
            />
            <RACButton className="cim-combo-box-button">
              <IconChevronDown />
            </RACButton>
          </RACGroup>

          <FormFieldError>{error}</FormFieldError>
          <FormFieldDescription>{description}</FormFieldDescription>

          <TagFieldTags<T>
            inputId={inputId}
            selectedKeys={selectedSet}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            renderEmptyState={renderEmptyState}
            onRemove={onItemSelectionChange}
          />

          <CollapsibleList
            offset={5} // 1px border + 4px actual offset
            maxHeight={315}
          >
            <TagFieldListBox<T>
              inputId={inputId}
              items={items}
              selectedKeys={selectedSet}
              setSelectedKeys={setSelectedKeys}
              onScroll={onScroll}
              isVirtualized={isVirtualized}
            >
              {children}
            </TagFieldListBox>
          </CollapsibleList>
        </RACProvider>
      </RACComboBox>

      {/* This input is used for native form validation. We can't use the combo box input because its value has no relation to the selected values. */}
      <RACInput
        ref={formInputRef}
        style={{ display: 'none' }}
        value={selectedSet.size || ''}
        required={isRequired}
        aria-invalid={validationState.displayValidation.isInvalid || undefined}
        onChange={() => {}}
      />

      {/* These hidden inputs provide values when a form is submitted. */}
      {name &&
        [...selectedSet].map((value) => <input type="hidden" key={value} form={form} name={name} value={value} />)}
    </>
  );
}

/**
 * Allows users to filter a collapsible list and select multiple items from it.
 *
 * See [tag field usage guidelines](https://ui.cimpress.io/components/tag-field).
 */
const _TagField = withStyleProps(forwardRef(TagField), 'TagField');

export { _TagField as TagField };

export interface TagFieldItemProps<T extends CollectionItem> extends Pick<
  RACListBoxItemProps<T>,
  'isDisabled' | 'onHoverStart' | 'onHoverEnd'
> {
  /** The ID of the item. Has to be unique across all sections and items. */
  id?: Key;
  /** The content to display as the label. */
  children: StringLikeChildren;
}

/** Renders a single list item within `TagField`. */
export function TagFieldItem<T extends CollectionItem>({ children, ...props }: TagFieldItemProps<T>) {
  const textValue =
    typeof children === 'string' ? children : typeof children === 'number' ? String(children) : children.join('');

  return (
    <RACListBoxItem {...props} className="cim-tag-field-item cim-checkbox-indicator-wrapper" textValue={textValue}>
      {({ isDisabled }) => (
        <>
          <CheckboxIndicator />

          <RACText slot="label" className={textStyle({ variant: 'medium', tone: isDisabled ? 'muted' : 'base' })}>
            {children}
          </RACText>
        </>
      )}
    </RACListBoxItem>
  );
}

TagFieldItem.displayName = 'TagFieldItem';

export interface TagFieldSectionProps<T extends CollectionItem> extends CollectionProps<T> {
  /** The ID of the section. Has to be unique across all sections and items. */
  id?: Key;
  /** The content to display as the section title. */
  title: string;
}

/** Groups list items within `TagField` into a section. */
export function TagFieldSection<T extends CollectionItem>({
  title,
  children,
  items,
  ...props
}: TagFieldSectionProps<T>) {
  if (!isProduction() && items != null && typeof children !== 'function') {
    console.warn('`TagFieldSection` requires `children` to be a function when using `items` prop');
  }

  return (
    <RACListBoxSection {...props} className="cim-combo-box-section">
      <RACHeader
        className={clsx('cim-combo-box-section-header', textStyle({ variant: 'small-semibold', tone: 'subtle' }))}
      >
        {title}
      </RACHeader>
      <RACCollection items={items}>{children}</RACCollection>
    </RACListBoxSection>
  );
}

TagFieldSection.displayName = 'TagFieldSection';
